import { parse, stringify, type ParsedUrlQueryInput } from 'node:querystring';

export interface RepairDetailsQuery {
  email?: string;
  /** Legacy URL field; maps to BFF `accessCode` at the client boundary. */
  code?: string;
  sn?: string;
  loginRequired?: boolean;
}

function readOptionalString(value: string | string[] | undefined): string | undefined {
  if (typeof value === 'string') return value;
  return undefined;
}

function readLoginRequired(value: string | string[] | undefined): boolean | undefined {
  if (value === undefined) return undefined;
  if (typeof value === 'string') return value === 'true' || value === '1';
  return undefined;
}

/** Legacy Breitling-compatible base64 query payload for `/service/details/[params]`. */
export function encodeRepairDetailsQuery(params: RepairDetailsQuery): string {
  const payload: ParsedUrlQueryInput = {};
  if (params.email) payload.email = params.email;
  if (params.code) payload.code = params.code;
  if (params.sn) payload.sn = params.sn;
  if (params.loginRequired) payload.loginRequired = 'true';
  return Buffer.from(stringify(payload)).toString('base64');
}

export function decodeRepairDetailsQuery(encoded: string): RepairDetailsQuery | null {
  try {
    const parsed = parse(Buffer.from(encoded, 'base64').toString('utf8'));
    const loginRequired = readLoginRequired(parsed.loginRequired);

    return {
      email: readOptionalString(parsed.email),
      code: readOptionalString(parsed.code),
      sn: readOptionalString(parsed.sn),
      loginRequired,
    };
  } catch {
    return null;
  }
}

export function buildRepairDetailsPath(params: RepairDetailsQuery): string {
  return `/service/details/${encodeURIComponent(encodeRepairDetailsQuery(params))}`;
}

/** Legacy param rule: `(code && email) || (sn && email)`. */
export function isValidRepairDetailsQuery(params: RepairDetailsQuery): boolean {
  const email = params.email?.trim();
  if (!email) return false;
  return Boolean((params.code?.trim() && email) || (params.sn?.trim() && email));
}
